import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { fmt2 } from '@/lib/utils';
import {
  getStandardMaterials, createStandardMaterial, updateStandardMaterial,
  deleteStandardMaterial, uploadStandardMaterials, getStandardMaterialsTemplate,
  getStandardMaterialMatches, getSites
} from '@/lib/api';
import {
  Plus, Upload, Download, Pencil, Trash2, Search, TrendingDown, Package, ArrowRight, FileSpreadsheet, Building2
} from 'lucide-react';

// Embeddable content component (no Header/Footer) for use inside MatchingDashboardPage
export const StandardMaterialsContent = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ manufacturer_name: '', manufacturer_part_number: '', material_description: '', ek_price_eur: '', ek_price_uom: '', fsc: false, food_safe: false });
  const [activeTab, setActiveTab] = useState('list');
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('all');
  const canManageSites = user?.role === 'admin' || user?.role === 'group_admin';

  useEffect(() => {
    if (canManageSites && user?.group_id) {
      getSites(user.group_id).then(res => setSites(res.data)).catch(() => {});
    }
  }, [canManageSites, user?.group_id]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedSiteId && selectedSiteId !== 'all') params.site_id = selectedSiteId;
      const [itemsRes, matchesRes] = await Promise.all([
        getStandardMaterials(params),
        getStandardMaterialMatches()
      ]);
      setItems(itemsRes.data);
      setMatches(matchesRes.data);
    } catch (err) {
      if (err.response?.status !== 401) toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [selectedSiteId]);

  useEffect(() => { loadData(); }, [loadData]);

  const openAdd = () => {
    setEditItem(null);
    setForm({ manufacturer_name: '', manufacturer_part_number: '', material_description: '', ek_price_eur: '', ek_price_uom: '', fsc: false, food_safe: false });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      manufacturer_name: item.manufacturer_name || '',
      manufacturer_part_number: item.manufacturer_part_number || '',
      material_description: item.material_description || '',
      ek_price_eur: item.ek_price_eur != null ? String(item.ek_price_eur) : '',
      ek_price_uom: item.ek_price_uom || '',
      fsc: item.fsc || false,
      food_safe: item.food_safe || false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.manufacturer_name.trim() || !form.manufacturer_part_number.trim()) {
      toast.error('Hersteller und Artikelnummer sind Pflichtfelder');
      return;
    }
    const payload = {
      ...form,
      ek_price_eur: form.ek_price_eur ? parseFloat(form.ek_price_eur) : null,
    };
    if (!editItem && selectedSiteId && selectedSiteId !== 'all') {
      payload.site_id = selectedSiteId;
    }
    try {
      if (editItem) {
        await updateStandardMaterial(editItem.item_id, payload);
        toast.success('Eintrag aktualisiert');
      } else {
        await createStandardMaterial(payload);
        toast.success('Eintrag hinzugefügt');
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      await deleteStandardMaterial(itemId);
      toast.success('Eintrag gelöscht');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadStandardMaterials(file);
      toast.success(res.data.message);
      if (res.data.errors?.length) {
        res.data.errors.forEach(err => toast.error(err));
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    }
    e.target.value = '';
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await getStandardMaterialsTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'standard_materialien_vorlage.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Template-Download fehlgeschlagen');
    }
  };

  const filtered = items.filter(item => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return item.manufacturer_name?.toLowerCase().includes(q) ||
           item.manufacturer_part_number?.toLowerCase().includes(q) ||
           item.material_description?.toLowerCase().includes(q);
  });

  const totalSavings = matches.reduce((sum, m) => {
    if (m.saving_per_unit && m.saving_per_unit > 0 && m.sku?.quantity) {
      return sum + (m.saving_per_unit * m.sku.quantity);
    }
    return sum;
  }, 0);

  if (!user) return null;

  return (
    <div className="space-y-6" data-testid="standard-materials-content">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold" data-testid="standard-materials-title">Standard-Materialliste</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Verwalten Sie Ihre Werks-Standardmaterialien und sehen Sie mögliche Einsparungen
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {canManageSites && sites.length > 0 && (
            <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
              <SelectTrigger className="w-[180px]" data-testid="site-selector">
                <Building2 className="h-4 w-4 mr-1 shrink-0" />
                <SelectValue placeholder="Werk wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Werke</SelectItem>
                {sites.map(s => (
                  <SelectItem key={s.site_id} value={s.site_id}>{s.site_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
            <Download className="h-4 w-4 mr-1" /> Vorlage
          </Button>
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="upload-btn">
            <Upload className="h-4 w-4 mr-1" /> Excel-Upload
          </Button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} />
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={openAdd} data-testid="add-standard-material-btn">
            <Plus className="h-4 w-4 mr-1" /> Hinzufügen
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="total-standard-count">{items.length}</p>
                <p className="text-xs text-muted-foreground">Standard-Materialien</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Package className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="matches-count">{matches.length}</p>
                <p className="text-xs text-muted-foreground">Treffer auf Plattform</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <TrendingDown className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600" data-testid="total-savings">
                  {totalSavings > 0 ? `${fmt2(totalSavings)} EUR` : '-'}
                </p>
                <p className="text-xs text-muted-foreground">Mögliche Einsparungen</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sub-Tab Switcher */}
      <div className="flex gap-1 border-b">
        <button
          onClick={() => setActiveTab('list')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'list' ? 'border-orange-500 text-orange-600' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-list"
        >
          Materialliste ({items.length})
        </button>
        <button
          onClick={() => setActiveTab('matches')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'matches' ? 'border-orange-500 text-orange-600' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-matches"
        >
          Treffer & Savings ({matches.length})
        </button>
      </div>

      {/* Search (list tab only) */}
      {activeTab === 'list' && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Suche nach Hersteller, Artikelnummer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="standard-materials-search"
          />
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
        </div>
      ) : activeTab === 'list' ? (
        filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                {items.length === 0 ? 'Noch keine Standard-Materialien hinterlegt' : 'Keine Treffer für die Suche'}
              </p>
              {items.length === 0 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-4 w-4 mr-1" /> Excel hochladen
                  </Button>
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={openAdd}>
                    <Plus className="h-4 w-4 mr-1" /> Manuell hinzufügen
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="standard-materials-table">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Hersteller</th>
                    <th className="text-left px-4 py-3 font-medium">Artikelnummer</th>
                    <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Beschreibung</th>
                    <th className="text-right px-4 py-3 font-medium">EK-Preis</th>
                    <th className="text-left px-4 py-3 font-medium">UoM</th>
                    <th className="text-center px-4 py-3 font-medium">FSC</th>
                    <th className="text-center px-4 py-3 font-medium">Food Safe</th>
                    <th className="text-center px-4 py-3 font-medium">Treffer</th>
                    <th className="text-right px-4 py-3 font-medium">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map((item) => {
                    const matchCount = matches.filter(m =>
                      m.standard_item?.item_id === item.item_id
                    ).length;
                    return (
                      <tr key={item.item_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`standard-row-${item.item_id}`}>
                        <td className="px-4 py-3 font-medium">{item.manufacturer_name}</td>
                        <td className="px-4 py-3">{item.manufacturer_part_number}</td>
                        <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell max-w-[200px] truncate">{item.material_description}</td>
                        <td className="px-4 py-3 text-right font-medium">
                          {item.ek_price_eur != null ? `${fmt2(item.ek_price_eur)} EUR` : '-'}
                        </td>
                        <td className="px-4 py-3">{item.ek_price_uom || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          {item.fsc ? <Badge className="bg-green-600 text-white text-xs">FSC</Badge> : <span className="text-muted-foreground">-</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.food_safe ? <Badge className="bg-blue-600 text-white text-xs">Food Safe</Badge> : <span className="text-muted-foreground">-</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {matchCount > 0 ? (
                            <Badge className="bg-green-500 text-white">{matchCount}</Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`edit-std-${item.item_id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(item.item_id)} data-testid={`delete-std-${item.item_id}`}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        /* Matches Tab */
        matches.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">Keine Treffer gefunden</p>
              <p className="text-xs text-muted-foreground mt-1">Sobald passende Materialien auf der Plattform verfügbar sind, erscheinen sie hier.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {matches.map((match, idx) => {
              const saving = match.saving_per_unit;
              const hasSaving = saving != null && saving > 0;
              return (
                <Card key={idx} className={hasSaving ? 'border-green-200 dark:border-green-800' : ''} data-testid={`match-card-${idx}`}>
                  <CardContent className="py-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Ihr Standard-Material</p>
                        <p className="font-medium truncate">{match.standard_item?.manufacturer_name} / {match.standard_item?.manufacturer_part_number}</p>
                        {match.ek_price_eur != null && (
                          <p className="text-sm text-muted-foreground">EK-Preis: {fmt2(match.ek_price_eur)} EUR/{match.standard_item?.ek_price_uom || 'Einheit'}</p>
                        )}
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0 hidden sm:block" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Plattform-Material</p>
                        <p className="font-medium truncate">{match.platform_material?.common_title || match.platform_material?.manufacturer_part_number}</p>
                        <p className="text-sm text-muted-foreground">
                          {match.sku?.quantity ? `${fmt2(match.sku.quantity)} ${match.sku.uom || ''}` : ''}
                          {match.sku?.book_value_kEUR ? ` | ${fmt2(match.sku.book_value_kEUR * 1000)} EUR` : ''}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        {hasSaving ? (
                          <div>
                            <Badge className="bg-green-500 text-white gap-1" data-testid={`saving-badge-${idx}`}>
                              <TrendingDown className="h-3 w-3" />
                              {fmt2(saving)} EUR/Einheit
                            </Badge>
                            {match.sku?.quantity && (
                              <p className="text-xs text-green-600 mt-1 font-medium">
                                Gesamt: {fmt2(saving * match.sku.quantity)} EUR
                              </p>
                            )}
                          </div>
                        ) : saving != null && saving <= 0 ? (
                          <Badge variant="outline" className="text-muted-foreground">Kein Saving</Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">EK fehlt</Badge>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => window.location.href = `/material/${match.platform_material?.material_id}`}
                        data-testid={`view-match-${idx}`}
                      >
                        Ansehen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="standard-material-dialog">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Material bearbeiten' : 'Standard-Material hinzufügen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Hersteller *</Label>
              <Input value={form.manufacturer_name} onChange={(e) => setForm(f => ({ ...f, manufacturer_name: e.target.value }))} placeholder="z.B. 3M" data-testid="std-form-manufacturer" />
            </div>
            <div>
              <Label>Hersteller-Artikelnummer *</Label>
              <Input value={form.manufacturer_part_number} onChange={(e) => setForm(f => ({ ...f, manufacturer_part_number: e.target.value }))} placeholder="z.B. VHB-4910" data-testid="std-form-part-number" />
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Input value={form.material_description} onChange={(e) => setForm(f => ({ ...f, material_description: e.target.value }))} placeholder="Optionale Beschreibung" data-testid="std-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>EK-Preis (EUR)</Label>
                <Input type="number" step="0.01" value={form.ek_price_eur} onChange={(e) => setForm(f => ({ ...f, ek_price_eur: e.target.value }))} placeholder="12.50" data-testid="std-form-ek-price" />
              </div>
              <div>
                <Label>Mengeneinheit</Label>
                <Input value={form.ek_price_uom} onChange={(e) => setForm(f => ({ ...f, ek_price_uom: e.target.value }))} placeholder="sqm, kg, pce" data-testid="std-form-uom" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="fsc" 
                  checked={form.fsc} 
                  onCheckedChange={(checked) => setForm(f => ({ ...f, fsc: !!checked }))} 
                  data-testid="std-form-fsc"
                />
                <Label htmlFor="fsc" className="cursor-pointer">FSC-zertifiziert</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="food_safe" 
                  checked={form.food_safe} 
                  onCheckedChange={(checked) => setForm(f => ({ ...f, food_safe: !!checked }))} 
                  data-testid="std-form-food-safe"
                />
                <Label htmlFor="food_safe" className="cursor-pointer">Food Safe</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={handleSave} data-testid="std-form-save-btn">
              {editItem ? 'Speichern' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Full-page wrapper (legacy route support)
export const StandardMaterialsPage = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        <StandardMaterialsContent />
      </main>
      <Footer />
    </div>
  );
};
